import apiClient, { handleApiResponse, handleApiError } from './apiClient';

/**
 * Authentication Service
 * Handles all authentication-related API calls with comprehensive logging
 */
class AuthService {
  constructor() {
    this.debugMode = import.meta.env.DEV; // Enabled debug logging in development
    this.log('AuthService initialized', { debugMode: this.debugMode });
  }

  /**
   * Enhanced logging with timestamps and context
   * @param {string} message - Log message
   * @param {object} data - Additional data to log
   * @param {string} level - Log level (info, warn, error)
   */
  log(message, data = {}, level = 'info') {
    const timestamp = new Date().toISOString();
    const logMessage = `[AuthService ${timestamp}] ${message}`;
    
    if (this.debugMode) {
      switch (level) {
        case 'error':
          console.error(logMessage, data);
          break;
        case 'warn':
          console.warn(logMessage, data);
          break;
        default:
          console.log(logMessage, data);
      }
    }
  }
  /**
   * Login user with email and password
   * @param {object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<object>} User data and token
   */
  async login(credentials) {
    const { email, password } = credentials;
    this.log('Login attempt started', { email, hasPassword: !!password });
    
    try {
      this.log('Sending login request to backend', { 
        endpoint: '/auth/login',
        baseURL: apiClient.defaults.baseURL 
      });
      
      const response = await apiClient.post('/auth/login', {
        email,
        password,
      });
      
      this.log('Login response received', { 
        status: response.status,
        hasData: !!response.data 
      });
      
      const data = handleApiResponse(response);
      
      this.log('Login response processed', { 
        hasToken: !!data.token,
        hasUser: !!data.user,
        userRole: data.user?.role 
      });
      
      // Store authentication token
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        this.log('Auth data stored successfully', { 
          tokenLength: data.token.length,
          userId: data.user?.id,
          userEmail: data.user?.email 
        });
      } else {
        this.log('No token received in response', data, 'warn');
      }
      
      this.log('Login completed successfully', { 
        authenticated: this.isAuthenticated(),
        storedUser: !!this.getStoredUser() 
      });
      
      return data;
    } catch (error) {
      this.log('Login failed', { 
        errorMessage: error.message,
        errorCode: error.response?.status,
        responseData: error.response?.data 
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * candidate login
   */
    async candidateLogin(credentials) {
    const { email, password } = credentials;
    this.log('Candidate login attempt started', { email, hasPassword: !!password });
    
    try {
      this.log('Sending candidate login request to backend', { 
        endpoint: '/auth/candidate/login',
        baseURL: apiClient.defaults.baseURL 
      });
      
      const response = await apiClient.post('/auth/candidate/login', {
        email,
        password,
      });
      
      this.log('Login response received', { 
        status: response.status,
        hasData: !!response.data 
      });
      
      const data = handleApiResponse(response);
      
      this.log('Login response processed', { 
        hasToken: !!data.token,
        hasUser: !!data.user,
        userRole: data.user?.role 
      });
      
      // Store authentication token
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        this.log('Auth data stored successfully', { 
          tokenLength: data.token.length,
          userId: data.user?.id,
          userEmail: data.user?.email 
        });
      } else {
        this.log('No token received in response', data, 'warn');
      }
      
      this.log('Login completed successfully', { 
        authenticated: this.isAuthenticated(),
        storedUser: !!this.getStoredUser() 
      });
      
      return data;
    } catch (error) {
      this.log('Login failed', { 
        errorMessage: error.message,
        errorCode: error.response?.status,
        responseData: error.response?.data 
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Register new user (HR staff)
   * @param {object} userData - User registration data
   * @returns {Promise<object>} User data and token
   */
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData);
      
      const data = handleApiResponse(response);
      
      // Store authentication token
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      
      return data;
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Register new candidate (public registration)
   * @param {object} candidateData - Candidate registration data
   * @returns {Promise<object>} User data and token
   */
  async candidateRegister(candidateData) {
    this.log('Candidate registration attempt started', { 
      email: candidateData.email,
      hasPassword: !!candidateData.password 
    });
    
    try {
      this.log('Sending candidate registration request to backend', { 
        endpoint: '/auth/candidate/register',
        baseURL: apiClient.defaults.baseURL 
      });
      
      const response = await apiClient.post('/auth/candidate/register', candidateData);
      
      this.log('Candidate registration response received', { 
        status: response.status,
        hasData: !!response.data 
      });
      
      const data = handleApiResponse(response);
      
      this.log('Candidate registration response processed', { 
        hasToken: !!data.token,
        hasUser: !!data.user,
        userType: data.user?.user_type 
      });
      
      // Store authentication token if returned
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        this.log('Candidate auth data stored successfully', { 
          tokenLength: data.token.length,
          userId: data.user?.id,
          userEmail: data.user?.email 
        });
      }
      
      this.log('Candidate registration completed successfully', { 
        authenticated: this.isAuthenticated(),
        storedUser: !!this.getStoredUser() 
      });
      
      return data;
    } catch (error) {
      this.log('Candidate registration failed', { 
        errorMessage: error.message,
        errorCode: error.response?.status,
        responseData: error.response?.data 
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Get current user profile
   * @returns {Promise<object>} Current user data
   */
  async getCurrentUser() {
    this.log('Getting current user from backend');
    
    try {
      const token = this.getToken();
      this.log('Current auth status', { 
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 10)}...` : null 
      });
      
      const response = await apiClient.get('/auth/me');
      
      this.log('Current user response received', { 
        status: response.status,
        hasData: !!response.data 
      });
      
      const data = handleApiResponse(response);
      
      this.log('Current user data processed', { 
        userId: data?.id,
        userEmail: data?.email,
        userRole: data?.role 
      });
      
      return data;
    } catch (error) {
      this.log('Get current user failed', { 
        errorMessage: error.message,
        errorCode: error.response?.status,
        isUnauthorized: error.response?.status === 401 
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Logout user
   */
  logout() {
    this.log('Logout initiated', { 
      wasAuthenticated: this.isAuthenticated(),
      hasStoredUser: !!this.getStoredUser() 
    });
    
    // Clear all auth data from storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    
    this.log('Auth data cleared', { 
      nowAuthenticated: this.isAuthenticated() 
    });
    
    // Prevent redirect loop by checking current location
    const currentPath = window.location.pathname;
    // Only redirect if not already on a login page
    if (currentPath !== '/login' && currentPath !== '/public/login') {
      this.log('Redirecting to appropriate login page', { fromPath: currentPath });
      // Redirect to appropriate login based on current path
      if (currentPath.startsWith('/public')) {
        window.location.href = '/public/login';
      } else {
        window.location.href = '/login';
      }
    } else {
      this.log('Already on login page, no redirect needed');
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const localToken = localStorage.getItem('authToken');
    const sessionToken = sessionStorage.getItem('authToken');
    const isAuth = !!(localToken || sessionToken);
    
    this.log('Authentication check', { 
      hasLocalToken: !!localToken,
      hasSessionToken: !!sessionToken,
      isAuthenticated: isAuth,
      tokenSource: localToken ? 'localStorage' : sessionToken ? 'sessionStorage' : 'none'
    });
    
    return isAuth;
  }

  /**
   * Get stored user data
   * @returns {object|null} User data or null
   */
  getStoredUser() {
    const userStr = localStorage.getItem('user');
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  /**
   * Get stored auth token
   * @returns {string|null} Auth token or null
   */
  getToken() {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  }
}

export default new AuthService();